import { useEffect, useState } from "react";
import type { UserTokenDTO } from "../../shared/types";
import { get, post } from "../lib/api";
import { Table } from "./Table";
import { confirmDialog } from "./Dialog";
import { Button, ErrorText, inputCls, timeAgo } from "./ui";

const EXPIRIES: [string, number | null][] = [["90 days", 90], ["No expiry", null]];

/**
 * A person's or a service account's named tokens: the list, a new one whose value is shown exactly once, and a
 * revoke. On About me it is the person's own (`/api/me/tokens`); in Admin → Users an admin manages a service
 * account's. Nobody, an admin included, manages another person's: a token signs in as its person (D15).
 */
export function Tokens({ userId, self = false }: { userId: string; self?: boolean }) {
  const base = self ? "/api/me/tokens" : `/api/users/${userId}/tokens`;
  const [tokens, setTokens] = useState<UserTokenDTO[]>([]);
  const [label, setLabel] = useState("");
  const [days, setDays] = useState<number | null>(90);
  const [shown, setShown] = useState<{ label: string; value: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const load = () => get<UserTokenDTO[]>(base).then(setTokens, (err) => setError((err as Error).message));
  useEffect(() => {
    void load();
  }, [base]);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const made = await post<{ token: UserTokenDTO; value: string }>(base, { label, expiresInDays: days });
      setShown({ label: made.token.label, value: made.value });
      setLabel("");
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const revoke = async (t: UserTokenDTO) => {
    if (!(await confirmDialog({ title: `Revoke "${t.label}"?`, body: "Anything signed in with it is signed out at its next request.", confirm: "Revoke token", danger: true }))) return;
    try {
      await post(`/api/users/${userId}/tokens/${t.id}/revoke`, {});
      await load();
    } catch (err) {
      setError((err as Error).message);
    }
  };
  const state = (t: UserTokenDTO) => (t.revokedAt ? "revoked" : t.expiresAt && Date.parse(t.expiresAt) < Date.now() ? "expired" : "live");

  return (
    <div className="space-y-4">
      {shown && (
        <div className="rounded-md border border-line bg-fill-subtle p-3 text-sm">
          <div className="mb-1 font-medium">{shown.label} — shown once</div>
          <div className="mb-2 text-xs text-muted">Copy it now and keep it where you keep secrets. AgentOS keeps only a fingerprint and cannot show it again.</div>
          <div className="flex items-center gap-2">
            <code className="min-w-0 flex-1 break-all rounded bg-surface px-2 py-1 text-xs">{shown.value}</code>
            <Button size="sm" onClick={() => void navigator.clipboard?.writeText(shown.value)}>Copy</Button>
            <Button size="sm" variant="ghost" onClick={() => setShown(null)}>Done</Button>
          </div>
        </div>
      )}
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (label.trim()) void create();
        }}
      >
        <label className="min-w-0 flex-1 text-xs font-medium text-muted">
          Label
          <input className={`${inputCls} mt-1`} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="What will use it, e.g. the forms smoke" />
        </label>
        <label className="text-xs font-medium text-muted">
          Expires
          <select className={`${inputCls} mt-1`} value={days === null ? "never" : String(days)} onChange={(e) => setDays(e.target.value === "never" ? null : Number(e.target.value))}>
            {EXPIRIES.map(([name, d]) => (
              <option key={name} value={d === null ? "never" : String(d)}>{name}</option>
            ))}
          </select>
        </label>
        <Button variant="primary" disabled={busy || !label.trim()}>{busy ? "Making…" : "New token"}</Button>
      </form>
      <ErrorText error={error} />
      <Table
        rows={tokens}
        rowKey={(t) => t.id}
        empty="No tokens yet."
        columns={[
          { header: "Label", primary: true, cell: (t) => <span className={state(t) === "live" ? "" : "text-faint line-through"}>{t.label}</span> },
          { header: "Token", className: "font-mono text-xs text-muted", cell: (t) => (t.hint ? `pat_…${t.hint}` : "—") },
          { header: "Created", className: "text-xs text-muted", cell: (t) => timeAgo(t.createdAt) },
          { header: "Last used", className: "text-xs text-muted", cell: (t) => (t.lastUsedAt ? timeAgo(t.lastUsedAt) : "never") },
          { header: "Expires", className: "text-xs text-muted", cell: (t) => (t.expiresAt ? new Date(t.expiresAt).toLocaleDateString() : "never") },
          { header: "State", className: "text-xs text-muted", cell: (t) => state(t) },
          { header: "", align: "right", cell: (t) => (state(t) === "live" ? <Button variant="danger" size="sm" onClick={() => void revoke(t)}>Revoke</Button> : null) },
        ]}
      />
    </div>
  );
}
